import {
  AdminDatePickerModal,
  AdminHeader,
  AdminSelect,
  Box,
  Break,
  Button,
  Chevron,
  Disclaimer,
  Div,
  ExportIcon,
  H3,
  H5,
  HeaderIconWrapper,
  Icon,
  Modal,
  P,
  Pill,
  SlidersIcon,
  StatusMark,
  SubTitleWithTooltipIcon,
  TooltipWrapper,
  centsToDollars,
  displayCentsInDollars,
  formatter,
  isAdmin,
  isProd,
  lightTheme,
  renderIncomeStatusName,
  renderIncomeTypeName,
} from '@vgrubs/components';
import {
  AgentIncomeType,
  IncomeStatus,
  IncomeType,
  NewTransactionResponse,
  vDriveDepositEntity,
  vDriveTransactionEntity,
  vGrubsIncomeEntity,
  vGrubsWithdrawalEntity,
  vOrdersIncomeType,
} from 'components/src/types/api/wallet.types';
import { DateTime } from 'luxon';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Collapsible,
  IconBalance,
  amountColorByValue,
  showAmountColor,
  showPlusMinus,
  showStatusColor,
  showTransactionIcon,
  showTransactionTitle,
} from './TransactonsStyled';

export const showIncomeStatusColor = (status: IncomeStatus | undefined) => {
  switch (status) {
    case IncomeStatus.pending:
      return lightTheme.yellow;
    case IncomeStatus.available:
      return lightTheme.green;
    default:
      return lightTheme.lightGray;
  }
};

// Per-row visual for the merchant/admin income list and detail modal:
// title text + amount sign + amount colour. Balance corrections show their own
// label, and balance deductions are signed `-` and coloured red.
export const describeIncomeRow = (obj: vGrubsIncomeEntity | undefined) => {
  switch (obj?.type) {
    case vOrdersIncomeType.balanceIncrementation:
      return {
        title: 'Balance Correction',
        sign: '+',
        amountColor: obj?.amount ? lightTheme.green : lightTheme.darkGray,
      };
    case vOrdersIncomeType.balanceDeduction:
      return {
        title: 'Balance Correction',
        sign: '-',
        amountColor: obj?.amount ? lightTheme.primary : lightTheme.darkGray,
      };
    default:
      return {
        // Orders carry their id in the title so a row can be matched to the
        // order without opening the detail modal. Older/manual incomes have no
        // orderId - those keep the plain label.
        title: obj?.orderId ? `New Income - #${obj.orderId}` : 'New Income',
        sign: obj?.amount && obj?.amount > 0 ? '+' : '',
        amountColor: amountColorByValue(obj?.amount),
      };
  }
};

// Marks an income from a cash pickup order: the customer paid the restaurant,
// and the (negative) amount is our fee taken from the vGrubs balance.
export const CashOrderTag = () => (
  <Pill
    $background={lightTheme.gray}
    $minWidth="0"
    $minWidthSM="0"
    $height="20px"
    $padding="0 8px"
    style={{ flex: 'none' }}
  >
    <Disclaimer $m="0" $bold $noWrap $color={lightTheme.darkGray}>
      Cash order
    </Disclaimer>
  </Pill>
);

// Shown wherever a transaction amount can be missing. The backend leaves
// `amount` null until the earning is calculated from the provider report,
// which can take up to a few days. Keep this string in one place so the
// explanation is identical everywhere it appears.
export const AMOUNT_PENDING_TOOLTIP =
  'This transaction will be calculated once the marketplace report is received.';

// Renders a transaction amount, or — when the amount is not yet known
// (still being calculated) — a muted "Pending" label with an explanatory
// tooltip. Used everywhere a `vGrubsIncomeEntity`/pending amount is shown so
// the affordance stays consistent.
export const TransactionAmount = ({
  amount,
  sign,
  color,
}: {
  amount?: number | null;
  sign?: string;
  color?: string;
}) => {
  if (amount === null || amount === undefined) {
    return (
      <SubTitleWithTooltipIcon
        text="Pending"
        tooltip={AMOUNT_PENDING_TOOLTIP}
        textColor={lightTheme.lightGray}
        color={lightTheme.lightGray}
        mb="0"
      />
    );
  }

  return (
    <H5 $right $m="0" $color={color}>
      {sign}
      {displayCentsInDollars(amount)}
    </H5>
  );
};

// Generic collapsible date-group. Used by both the legacy Transactions page
// (TransactionDateComponent below) and the agent Transactions page.
export const TransactionDateGroup = <T,>({
  date,
  items,
  index,
  page,
  renderItem,
}: {
  date: string;
  items: T[];
  index: number;
  page: number;
  renderItem: (item: T, key: number) => ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  const isExpanded = expanded === date;

  const handleExpand = () => {
    setExpanded((prev) => (prev === date ? null : date));
    setHeight(ref?.current?.clientHeight ?? 0);
  };

  useEffect(() => {
    if (index === 0 && page === 2) {
      setExpanded(date);
      setHeight(ref?.current?.clientHeight ?? 0);
    }
    if (expanded) setHeight(ref?.current?.clientHeight ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, index, items]);

  return (
    <>
      <Box
        $flex
        $alignCenter
        $between
        $mb="8"
        $background={isExpanded && lightTheme.primary}
        $pointer
        onClick={handleExpand}
        $minHeight="40px"
      >
        <Div $flex $alignCenter $gap="8px">
          <P $bold $m="0" $color={isExpanded && lightTheme.white}>
            {DateTime.fromISO(date).toFormat('MMM dd, yyyy')}
          </P>
        </Div>
        <Icon
          $pointer
          $rotate={isExpanded ? '360deg' : '180deg'}
          color={isExpanded ? lightTheme.white : lightTheme.darkGray}
        >
          <Chevron />
        </Icon>
      </Box>

      <Collapsible expanded={isExpanded} elementHeight={height}>
        <div ref={ref}>{items.map((it, i) => renderItem(it, i))}</div>
      </Collapsible>
    </>
  );
};

export const TransactionDateComponent = ({
  item,
  setIncomeModal,
  setWithdrawalModal,
  setDepositModal,
  setTransactionsModal,
  index,
  page,
}: {
  item: { date: string; items: NewTransactionResponse[] };
  setIncomeModal?: Dispatch<
    SetStateAction<NewTransactionResponse<vGrubsIncomeEntity> | null>
  >;
  setWithdrawalModal?: Dispatch<
    SetStateAction<NewTransactionResponse<vGrubsWithdrawalEntity> | null>
  >;
  setDepositModal?: Dispatch<
    SetStateAction<NewTransactionResponse<vDriveDepositEntity> | null>
  >;
  setTransactionsModal?: Dispatch<
    SetStateAction<NewTransactionResponse<vDriveTransactionEntity> | null>
  >;
  index: number;
  page: number;
}) => (
  <TransactionDateGroup<NewTransactionResponse>
    date={item.date}
    items={item.items}
    index={index}
    page={page}
    renderItem={(row, i) => (
      <TransactionComponent
        key={i}
        item={row}
        setIncomeModal={setIncomeModal}
        setWithdrawalModal={setWithdrawalModal}
        setDepositModal={setDepositModal}
        setTransactionsModal={setTransactionsModal}
      />
    )}
  />
);

export const TransactionComponent = ({
  item,
  setIncomeModal,
  setWithdrawalModal,
  setDepositModal,
  setTransactionsModal,
}: {
  item: NewTransactionResponse<
    | vGrubsIncomeEntity
    | vGrubsWithdrawalEntity
    | vDriveDepositEntity
    | vDriveTransactionEntity
  >;
  setIncomeModal?: Dispatch<
    SetStateAction<NewTransactionResponse<vGrubsIncomeEntity> | null>
  >;
  setWithdrawalModal?: Dispatch<
    SetStateAction<NewTransactionResponse<vGrubsWithdrawalEntity> | null>
  >;
  setDepositModal?: Dispatch<
    SetStateAction<NewTransactionResponse<vDriveDepositEntity> | null>
  >;
  setTransactionsModal?: Dispatch<
    SetStateAction<NewTransactionResponse<vDriveTransactionEntity> | null>
  >;
}) => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'Incomes';

  const handleOpenModal = () => {
    if (type === 'Incomes')
      return setIncomeModal?.(
        item as NewTransactionResponse<vGrubsIncomeEntity>
      );
    if (type === 'Withdrawals')
      return setWithdrawalModal?.(
        item as NewTransactionResponse<vGrubsWithdrawalEntity>
      );
    if (type === 'Deposits')
      return setDepositModal?.(
        item as NewTransactionResponse<vDriveDepositEntity>
      );
    if (type === 'Transactions')
      return setTransactionsModal?.(
        item as NewTransactionResponse<vDriveTransactionEntity>
      );
  };

  const renderStatusMark = () => {
    if (type === 'Incomes') {
      const incomeObject = item.object as vGrubsIncomeEntity;
      return (
        <StatusMark
          $color={showIncomeStatusColor(incomeObject?.status || undefined)}
        />
      );
    }
    if (type === 'Deposits' || type === 'Transactions') {
      return <StatusMark $color={lightTheme.green} />;
    }
    if (type === 'Withdrawals') {
      return <StatusMark $color={showStatusColor(item?.status)} />;
    }
    return null;
  };

  const incomeDescriptor =
    type === 'Incomes'
      ? describeIncomeRow(item.object as vGrubsIncomeEntity)
      : null;
  const title = incomeDescriptor?.title ?? showTransactionTitle(type);
  const sign = incomeDescriptor?.sign ?? showPlusMinus(type);
  const amountColor = incomeDescriptor?.amountColor ?? showAmountColor(type);

  return (
    <Box
      $relative
      $flex
      $alignCenter
      $between
      $mb="8"
      $pointer
      onClick={handleOpenModal}
    >
      {renderStatusMark()}
      <Div $flex $alignCenter $gap="20px" $gapSM="16px">
        {type === 'Incomes' &&
        (item.object as vGrubsIncomeEntity)?.type ===
          vOrdersIncomeType.balanceDeduction ? (
          <IconBalance />
        ) : (
          showTransactionIcon(type)
        )}
        <Div $width="100%" $pointer>
          <Div $flex $alignCenter $gap="8px" $wrap>
            <P $m="0">{title}</P>
            {type === 'Incomes' &&
              (item.object as vGrubsIncomeEntity)?.cashPayment && (
                <CashOrderTag />
              )}
          </Div>
          <Disclaimer $color={lightTheme.lightGray} $mb="0">
            {DateTime.fromISO(item.createdAt).toFormat('hh:mm a')}
          </Disclaimer>
        </Div>
      </Div>
      <Div>
        <H5 $right $mb="0" $color={amountColor}>
          {sign}
          {formatter.format(centsToDollars(item.amount))}
        </H5>
      </Div>
    </Box>
  );
};

export type IncomeFilterOption = { name: string; value: string };

// Computed lazily — the helpers live in the barrel, and evaluating these at
// module load triggers a TDZ error when this file is loaded before helpers.
export const getAgentIncomeTypeOptions = (): IncomeFilterOption[] => [
  { name: 'All Types', value: '' },
  ...Object.values(AgentIncomeType).map((value) => ({
    name: renderIncomeTypeName(value, true) ?? value,
    value,
  })),
];

export const getIncomeTypeOptions = (): IncomeFilterOption[] => [
  { name: 'All Types', value: '' },
  ...Object.values(IncomeType)
    .filter((value) => !isProd || value !== IncomeType.vGrubsAI)
    .map((value) => ({
      name: renderIncomeTypeName(value) ?? value,
      value,
    })),
];

export const getIncomeStatusOptions = (): IncomeFilterOption[] => [
  { name: 'All Statuses', value: '' },
  ...Object.values(IncomeStatus).map((value) => ({
    name: renderIncomeStatusName(value) ?? value,
    value,
  })),
];

// ---------------------------------------------------------------------------
// Shared page-level layout pieces used by both Transactions and TransactionsAgent
// ---------------------------------------------------------------------------

export const TransactionsPageHeader = ({
  dateInput,
  handleDateChange,
  handleDateApply,
  clearDate,
  hasDateFilter,
  dateLabel,
  onExport,
  incomeStatus,
  setIncomeStatus,
  incomeType,
  setIncomeType,
  showIncomeFilters,
  incomeStatusOptions,
  incomeTypeOptions,
  userName,
}: {
  dateInput: Date[] | string[];
  handleDateChange: (date: Date[] | string[]) => void;
  handleDateApply: () => void;
  clearDate?: () => void;
  hasDateFilter?: boolean;
  dateLabel: string;
  onExport?: () => void;
  incomeStatus?: string | null;
  setIncomeStatus?: (status: string | null) => void;
  incomeType?: string | null;
  setIncomeType?: (type: string | null) => void;
  showIncomeFilters?: boolean;
  incomeStatusOptions?: IncomeFilterOption[];
  incomeTypeOptions?: IncomeFilterOption[];
  userName?: string;
}) => {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [pendingIncomeStatus, setPendingIncomeStatus] = useState<
    string | null | undefined
  >(incomeStatus);
  const [pendingIncomeType, setPendingIncomeType] = useState<
    string | null | undefined
  >(incomeType);

  const resolvedStatusOptions = incomeStatusOptions ?? getIncomeStatusOptions();
  const resolvedTypeOptions = incomeTypeOptions ?? getAgentIncomeTypeOptions();

  useEffect(() => {
    setPendingIncomeStatus(incomeStatus);
  }, [incomeStatus]);

  useEffect(() => {
    setPendingIncomeType(incomeType);
  }, [incomeType]);

  const handleApply = () => {
    if (pendingIncomeStatus !== incomeStatus) {
      setIncomeStatus?.(pendingIncomeStatus ?? null);
    }
    if (pendingIncomeType !== incomeType) {
      setIncomeType?.(pendingIncomeType ?? null);
    }
    handleDateApply();
  };

  const hasActiveFilters = !!hasDateFilter || !!incomeStatus || !!incomeType;

  const handleClearFilters = () => {
    setPendingIncomeStatus(null);
    setPendingIncomeType(null);
    if (incomeStatus) setIncomeStatus?.(null);
    if (incomeType) setIncomeType?.(null);
    clearDate?.();
    setDatePickerOpen(false);
  };

  return (
    <>
      <AdminHeader
        title="Transactions"
        subtitle={isAdmin ? userName : undefined}
      >
        <Div $flex $alignCenter $gap="20px" $gapSM="10px">
          <TooltipWrapper tooltip="Export Report">
            <HeaderIconWrapper onClick={onExport}>
              <Icon $size="24" color={lightTheme.primary}>
                <ExportIcon />
              </Icon>
            </HeaderIconWrapper>
          </TooltipWrapper>
          <Div $flex $alignCenter $gap="8px">
            <AdminDatePickerModal
              mode="range"
              value={dateInput}
              onChange={handleDateChange}
              click={handleApply}
              isOpen={datePickerOpen}
              setIsOpen={setDatePickerOpen}
              $marker={hasActiveFilters}
              Icon={
                <Icon $size="20" color={lightTheme.primary}>
                  <SlidersIcon />
                </Icon>
              }
              customComponent={
                <>
                  <Div $flex $gap="12px" $column>
                    {showIncomeFilters && (
                      <Div $mt="16" $flex $gap="12px" $column>
                        <AdminSelect
                          label="Income Status"
                          value={pendingIncomeStatus ?? ''}
                          options={
                            <>
                              {resolvedStatusOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.name}
                                </option>
                              ))}
                            </>
                          }
                          onChange={(e) =>
                            setPendingIncomeStatus(
                              e.target.value === '' ? null : e.target.value
                            )
                          }
                          $textCenter
                        />
                        <AdminSelect
                          label="Income Type"
                          value={pendingIncomeType ?? ''}
                          options={
                            <>
                              {resolvedTypeOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.name}
                                </option>
                              ))}
                            </>
                          }
                          onChange={(e) =>
                            setPendingIncomeType(
                              e.target.value === '' ? null : e.target.value
                            )
                          }
                          $textCenter
                        />
                      </Div>
                    )}
                    {hasActiveFilters && (
                      <Button
                        $small
                        $selected
                        $gray
                        onClick={handleClearFilters}
                        $w="100%"
                        $wSM="100%"
                        $mb="-8"
                        $mt="8"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Div>
                </>
              }
            />
          </Div>
        </Div>
      </AdminHeader>
      <Div $mt="32" $mtSM="20" />
      <Div $mobile>
        <H5 $mb="0">{dateLabel}</H5>
      </Div>
    </>
  );
};

export const TransactionsExportConfirmModal = ({
  isOpen,
  closeModal,
  onConfirm,
  dateLabel,
}: {
  isOpen: boolean;
  closeModal: () => void;
  onConfirm: () => void;
  dateLabel: string;
}) => (
  <Modal
    isOpen={isOpen}
    closeModal={closeModal}
    logo={false}
    exitButton={false}
  >
    <H3 $m="10px" $center>
      Export Report
    </H3>
    <P $mb="24" $center>
      Generate a CSV report for <Break $mobile /> <strong>{dateLabel}</strong>?
    </P>

    <Div $flex $gap="16px">
      <Button
        onClick={() => {
          onConfirm();
          closeModal();
        }}
        $w="100%"
        $selected
      >
        Yes
      </Button>
      <Button $w="100%" onClick={closeModal} $gray $selected>
        No
      </Button>
    </Div>
  </Modal>
);
